import { ChangeEvent, FormEvent, ReactElement } from "react";
import { Form } from "@justworkshr/milo-form";
import { Button } from "@justworkshr/milo-core";

import {
  UserRegistrationForm,
  TextInputField,
  InlineError,
} from "../../types/userRegistrationForm";
import { RegistrationInputValidationMap } from "pages/register/types/userRegistrationForm";

import TextInputFormField from "../textInputFormField/TextInputFormField";
import FooterButtons from "../footerButtons/FooterButtons";

import styles from "./loginCredentialsForm.module.css";

const LoginCredentialsForm: React.FC<{
  formData: UserRegistrationForm;
  isLoading: boolean;
  inlineError?: InlineError;
  inputValidation: RegistrationInputValidationMap;
  onComplete: () => void;
  onCancel: () => void;
  onChangeInput: (inputField: TextInputField, value: string) => void;
}> = ({
  formData,
  isLoading,
  inlineError = {},
  inputValidation,
  onComplete,
  onCancel,
  onChangeInput,
}): ReactElement => {
  const formValid = () => {
    if (
      formData.username === "" ||
      formData.password === "" ||
      formData.passwordConfirmation === ""
    )
      return false;

    return [
      TextInputField.Username,
      TextInputField.Password,
      TextInputField.PasswordConfirmation,
    ].every((field) => inputValidation[field].isValid);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onComplete();
  };

  const changeHandler =
    (inputField: TextInputField) => (e: ChangeEvent<HTMLInputElement>) =>
      onChangeInput(inputField, e.target.value);

  return (
    <Form onSubmit={onSubmit}>
      <div className={styles.inputsContainer}>
        <TextInputFormField
          isRequired={true}
          formField={{
            label: "Pick a username",
            error: inlineError.username,
          }}
          textInput={{
            name: TextInputField.Username,
            value: formData.username,
            onChange: changeHandler(TextInputField.Username),
          }}
          validation={inputValidation.username}
        />

        <TextInputFormField
          isRequired={true}
          isPasswordInput={true}
          formField={{
            label: "Pick a password",
            error: inlineError.password,
          }}
          textInput={{
            name: TextInputField.Password,
            value: formData.password,
            onChange: changeHandler(TextInputField.Password),
          }}
          validation={inputValidation.password}
        />

        <TextInputFormField
          isRequired={true}
          isPasswordInput={true}
          formField={{
            label: "Enter your password again",
            error: inlineError.passwordConfirmation,
          }}
          textInput={{
            name: TextInputField.PasswordConfirmation,
            value: formData.passwordConfirmation,
            onChange: changeHandler(TextInputField.PasswordConfirmation),
          }}
        />
      </div>

      <FooterButtons>
        <Button size="md" type="button" variant="ghost" onClick={onCancel}>
          Back
        </Button>
        <Button
          size="md"
          type="submit"
          rightIcon="arrow-right"
          disabled={!formValid()}
          onSubmit={onSubmit}
          loading={isLoading}
        >
          Next
        </Button>
      </FooterButtons>
    </Form>
  );
};

export default LoginCredentialsForm;
